import { createEffect, Icon, If, location, onMount, reactive } from 'cdui-js';
import { replaceTemplate } from '@china-difi/lib/template';
import {
  fetchAvaCurrencies,
  fetchCashBalancesByCurrency,
  fetchCurrencyRates,
  submitCurrencyExchange,
} from '../../../api/account';
import { fetchStockAccountsMeta } from '../../../api/funds';
import i18n from '../../../i18n';
import { goBackByBrowser, routeReplace } from '../../../lib/browser';
import { getUserInfo, isH5User } from '../../../stores/user';
import { showActionSheet } from '../components/ActionSheet';
import { ActionSheetCheckIcon } from '../components/ActionSheet/CheckIcon';
import { BtnLoading } from '../components/BtnLoading';
import { showModal } from '../components/Modal';
import { showToast } from '../components/Toast';
import {
  applyDepositAppQuery,
  bootDepositApp,
  getBackToAppUrl,
  getDepositAppQuery,
  isFundHideNav,
  snapshotDepositAppQuery,
  takeExchangePrefetch,
} from '../deposit/h5-deposit/appBridge';
import { gotoLogin } from '../utils/gotoLogin';
import { downArrowMuted as downArrow } from '../../../assets/commonIcons';
import exchangeIcon from '../../../assets/currency-exchange/exchange.svg';
import { CurrencyFlag } from '../../../components/CurrencyFlag';
import {
  accountLabel,
  currencyName,
  exchangeRecordsUrl,
  filterAccountsForEntryMode,
  hydrateFromExchangePrefetch,
  is2goNotLoggedIn,
  applyCashBalanceByCurrency,
  isUsableExchangePrefetch,
  overviewAccountsOf,
  parseBalance,
  parseRate,
  pickAccountNum,
  readExchangeQuery,
  sanitizeAmount,
  sortAccounts,
  toExchangeAccount,
  truncDiv,
  truncMul,
  unwrapCurrencyList,
  withExchangeQuery,
  type ExchangeAccount,
} from './shared';
import './exchange.css';

type PageMode = 'form' | 'success';

type SuccessSnapshot = {
  fromCurrency: string;
  toCurrency: string;
  fromAmount: string;
  toAmount: string;
  rate: number;
};

let syncingFrom = false;
let syncingTo = false;
let rateReqId = 0;
let balReqId = 0;
/** App 预拉汇率对应的币对；createEffect 命中时跳过首屏 rates 请求，避免「汇率加载中」闪一下 */
let seededRatePair = '';
/** App 预拉可兑金额 key：`accountNum|fromCurrency`；命中时跳过首屏余额请求 */
let seededBalanceKey = '';

const IconBack = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 3L5 8l5 5"
      stroke="#1B212D"
      stroke-width="1.6"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  </svg>
);

export const CurrencyExchange = () => {
  const state = reactive({
    mode: 'form' as PageMode,
    /** 仅页内 API（切账户）出骨架；首屏等 Token 用真实空态，不设 loading */
    loading: false,
    rateLoading: false,
    rate: 0,
    submitting: false,
    accounts: [] as ExchangeAccount[],
    currencies: [] as string[],
    accountNum: '',
    fromCurrency: '',
    toCurrency: '',
    fromAmount: '',
    toAmount: '',
    fromError: '',
    success: null as SuccessSnapshot | null,
  });

  const currentAccount = () => state.accounts.find((item) => item.accountNum === state.accountNum);

  const availableBal = () => {
    const account = currentAccount();
    if (!account || !state.fromCurrency) return '-';
    const bal = account.money[state.fromCurrency];
    return bal == null || bal === '' ? '-' : bal;
  };

  const rateLine = () => {
    if (!state.fromCurrency || !state.toCurrency) return '';
    if (state.rateLoading) return i18n.CurrencyExchange.RateLoading;
    if (!state.rate) return '-';
    return `1 ${currencyName(state.fromCurrency)} ≈ ${state.rate} ${currencyName(state.toCurrency)}`;
  };

  const accountTriggerText = () => {
    const row = currentAccount();
    return row ? row.label : i18n.FundH5.PleaseSelect;
  };

  onMount(() => {
    applyDepositAppQuery(snapshotDepositAppQuery());
    void loadAccounts();
  });

  createEffect(() => {
    const from = state.fromCurrency;
    const to = state.toCurrency;
    if (!from || !to || from === to) {
      state.rate = 0;
      state.rateLoading = false;
      return;
    }
    const pair = `${from}|${to}`;
    if (pair === seededRatePair && state.rate > 0) {
      state.rateLoading = false;
      return;
    }
    seededRatePair = '';
    const reqId = ++rateReqId;
    state.rateLoading = true;
    fetchCurrencyRates(from, to)
      .then((res) => {
        if (reqId !== rateReqId) return;
        state.rate = parseRate(res);
        if (state.fromAmount && state.rate > 0) {
          syncingTo = true;
          state.toAmount = truncMul(state.fromAmount, state.rate);
          syncingTo = false;
        }
      })
      .catch(() => {
        if (reqId !== rateReqId) return;
        state.rate = 0;
      })
      .finally(() => {
        if (reqId !== rateReqId) return;
        state.rateLoading = false;
      });
  });

  /** 兑出币可兑金额：仅 accountNum + fromCurrency；兑入币变化不请求 */
  createEffect(() => {
    const accountNum = state.accountNum;
    const currency = state.fromCurrency;
    if (!accountNum || !currency) return;
    const key = `${accountNum}|${currency}`;
    if (key === seededBalanceKey) return;
    seededBalanceKey = '';
    const reqId = ++balReqId;
    void fetchCashBalancesByCurrency(accountNum, currency)
      .then((bal) => {
        if (reqId !== balReqId) return;
        // data: null / '' 不能 Number()——Number(null)===0 会误显示可兑金额
        if (bal == null || bal === '') return;
        const n = typeof bal === 'number' ? bal : Number(bal);
        if (!Number.isFinite(n)) return;
        state.accounts = applyCashBalanceByCurrency(state.accounts, accountNum, currency, n);
      })
      .catch((error) => {
        if (reqId !== balReqId) return;
        if (is2goNotLoggedIn(error) && isH5User()) gotoLogin();
      });
  });

  /** 旧 App 无 exchange：自拉 meta + 币种；余额由 fromCurrency effect 拉 */
  const loadAccountsFromApi = async () => {
    try {
      await getUserInfo();
    } catch {
      // 无用户信息也继续拉账户列表
    }
    const list = filterAccountsForEntryMode(
      sortAccounts(overviewAccountsOf(await fetchStockAccountsMeta())).map(toExchangeAccount),
    );
    state.accounts = list;
    const wanted = pickAccountNum(list, readExchangeQuery().accountNum);
    if (wanted) {
      state.accountNum = wanted;
      await loadCurrencies(wanted);
    }
  };

  /** 用 APP_TO_WEB_TOKEN.payload.exchange 灌数；完整时零请求 */
  const applyExchangePrefetch = () => {
    const prefetch = takeExchangePrefetch();
    if (!isUsableExchangePrefetch(prefetch) || !prefetch) {
      console.log('[currency-exchange] 无可用 exchange 预拉，将自拉读接口', prefetch);
      return false;
    }

    const hydrated = hydrateFromExchangePrefetch(prefetch, readExchangeQuery().accountNum);
    console.log('[currency-exchange] 使用 APP_TO_WEB_TOKEN.exchange 灌数（不打读接口）', {
      accountNum: hydrated.accountNum,
      currencies: hydrated.currencies,
      fromCurrency: hydrated.fromCurrency,
      toCurrency: hydrated.toCurrency,
      rate: hydrated.rate,
      seededRatePair: hydrated.seededRatePair,
      seededBalanceKey: hydrated.seededBalanceKey,
      accounts: hydrated.accounts.length,
      incomplete: hydrated.needCurrencies || hydrated.needMoney,
    });
    seededRatePair = hydrated.seededRatePair;
    seededBalanceKey = hydrated.seededBalanceKey;
    state.accounts = hydrated.accounts;
    state.accountNum = hydrated.accountNum;
    state.currencies = hydrated.currencies;
    state.rate = hydrated.rate;
    state.rateLoading = false;
    state.fromCurrency = hydrated.fromCurrency;
    state.toCurrency = hydrated.toCurrency;
    // 缺字段也不首屏补拉；切账户 / 换兑出币时再走现有接口
    return true;
  };

  /** 首屏已出真实表单；等 APP_TO_WEB_TOKEN 后再灌数 / 自拉，不挡 UI */
  const loadAccounts = async () => {
    try {
      try {
        await bootDepositApp();
      } catch {
        // 浏览器调试继续用本地 token
      }
      const usedPrefetch = applyExchangePrefetch();
      if (!usedPrefetch) {
        console.log('[currency-exchange] 无 exchange 预拉，自拉读接口');
        await loadAccountsFromApi();
      }
    } catch (error) {
      if (is2goNotLoggedIn(error) && isH5User()) {
        gotoLogin();
        return;
      }
      showToast(i18n.FundH5.LoadFail);
    }
  };

  const clearPair = () => {
    seededRatePair = '';
    seededBalanceKey = '';
    state.fromCurrency = '';
    state.toCurrency = '';
    state.fromAmount = '';
    state.toAmount = '';
    state.rate = 0;
    state.fromError = '';
    state.currencies = [];
  };

  const loadCurrencies = async (accountNum: string) => {
    try {
      const res = await fetchAvaCurrencies(accountNum);
      const list = unwrapCurrencyList(res);
      state.currencies = list;
      state.fromCurrency = list[0] || '';
      state.toCurrency = list[1] || '';
    } catch (error) {
      state.currencies = [];
      if (is2goNotLoggedIn(error) && isH5User()) gotoLogin();
    }
  };

  const onAccountChange = async (accountNum: string) => {
    if (accountNum === state.accountNum) return;
    state.accountNum = accountNum;
    clearPair();
    state.loading = true;
    try {
      // 余额由 fromCurrency effect 在币种就绪后拉取
      await loadCurrencies(accountNum);
    } finally {
      state.loading = false;
    }
  };

  const openAccounts = () => {
    if (state.loading || !state.accounts.length) return;
    const sheet = showActionSheet({
      title: i18n.FundH5.SelectAccount,
      content: () => (
        <div>
          {state.accounts.map((item) => {
            const active = item.accountNum === state.accountNum;
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  void onAccountChange(item.accountNum);
                  sheet.close();
                }}
              >
                <div class="action-sheet-option-label">{item.label}</div>
                {active ? <ActionSheetCheckIcon /> : null}
              </div>
            );
          })}
        </div>
      ),
    });
  };

  const openCurrency = (side: 'from' | 'to') => {
    if (state.loading) return;
    const exclude = side === 'from' ? state.toCurrency : state.fromCurrency;
    const options = state.currencies.filter((code) => code !== exclude);
    if (!options.length) return;
    const sheet = showActionSheet({
      title: side === 'from' ? i18n.CurrencyExchange.SelectFromCurrency : i18n.CurrencyExchange.SelectToCurrency,
      content: () => (
        <div>
          {options.map((code) => {
            const active = code === (side === 'from' ? state.fromCurrency : state.toCurrency);
            return (
              <div
                class={`action-sheet-option${active ? ' action-sheet-option-active' : ''}`}
                onclick={() => {
                  if (side === 'from') {
                    state.fromCurrency = code;
                    if (state.toCurrency === code) state.toCurrency = '';
                  } else {
                    state.toCurrency = code;
                    if (state.fromCurrency === code) state.fromCurrency = '';
                  }
                  state.fromError = '';
                  sheet.close();
                }}
              >
                <div class="action-sheet-option-label exchange-curr-option">
                  <CurrencyFlag code={code} />
                  <span>{currencyName(code)}</span>
                </div>
                {active ? <ActionSheetCheckIcon /> : null}
              </div>
            );
          })}
        </div>
      ),
    });
  };

  const onFromInput = (e: Event) => {
    if (syncingFrom) return;
    const val = sanitizeAmount((e.target as HTMLInputElement).value);
    state.fromAmount = val;
    state.fromError = '';
    if (val && state.rate > 0) {
      syncingTo = true;
      state.toAmount = truncMul(val, state.rate);
      syncingTo = false;
    } else {
      state.toAmount = '';
    }
  };

  const onToInput = (e: Event) => {
    if (syncingTo) return;
    const val = sanitizeAmount((e.target as HTMLInputElement).value);
    state.toAmount = val;
    state.fromError = '';
    if (val && state.rate > 0) {
      syncingFrom = true;
      state.fromAmount = truncDiv(val, state.rate);
      syncingFrom = false;
    } else {
      state.fromAmount = '';
    }
  };

  const swapCurrencies = () => {
    const tmpC = state.fromCurrency;
    state.fromCurrency = state.toCurrency;
    state.toCurrency = tmpC;
    const tmpA = state.fromAmount;
    state.fromAmount = state.toAmount;
    state.toAmount = tmpA;
    state.fromError = '';
  };

  const tip = (message: string) => {
    showModal({
      title: i18n.FundH5.WarmTip,
      content: () => message,
      confirmText: i18n.FundH5.GotIt,
    });
  };

  const validateBeforeConfirm = () => {
    if (!state.accountNum) {
      showToast({ message: i18n.CurrencyExchange.ToastSelectAccount, hud: true });
      return false;
    }
    if (!state.fromCurrency) {
      tip(i18n.CurrencyExchange.ErrSelectFromCurrency);
      return false;
    }
    if (!state.toCurrency) {
      tip(i18n.CurrencyExchange.ErrSelectToCurrency);
      return false;
    }
    const amount = Number(state.fromAmount);
    if (!state.fromAmount || !Number.isFinite(amount) || amount <= 0) {
      state.fromError = i18n.CurrencyExchange.ErrEnterFromAmount;
      return false;
    }
    const bal = parseBalance(availableBal());
    if (bal != null && amount > bal) {
      state.fromError = i18n.CurrencyExchange.ErrExceedAvailable;
      return false;
    }
    state.fromError = '';
    return true;
  };

  const doSubmit = async (close: () => void) => {
    if (state.submitting) return;
    state.submitting = true;
    try {
      await submitCurrencyExchange({
        accountNum: state.accountNum,
        fromCurrency: state.fromCurrency,
        toCurrency: state.toCurrency,
        fromAmount: Number(state.fromAmount),
        toAmount: Number(state.toAmount),
        exchangeRate: state.rate,
      });
      state.success = {
        fromCurrency: state.fromCurrency,
        toCurrency: state.toCurrency,
        fromAmount: state.fromAmount,
        toAmount: state.toAmount,
        rate: state.rate,
      };
      state.fromAmount = '';
      state.toAmount = '';
      state.fromError = '';
      // 提交中 canClose 会拦住关闭，成功后先放开再关确定框
      state.submitting = false;
      close();
      state.mode = 'success';
    } catch (error) {
      const raw = typeof error === 'string' ? error : '';
      const msg = raw.replace(/^\d+\s*/, '').trim();
      if (!msg) showToast(i18n.CurrencyExchange.ExchangeFail);
    } finally {
      state.submitting = false;
    }
  };

  const openConfirm = () => {
    if (!validateBeforeConfirm()) return;
    const account = currentAccount();
    const sheet = showActionSheet({
      title: i18n.CurrencyExchange.ConfirmTitle,
      contentClass: 'exchange-confirm-sheet',
      canClose: () => !state.submitting,
      content: () => (
        <div class="exchange-confirm">
          <div class="confirm-rows">
            <div class="confirm-row">
              <span class="label">{i18n.CurrencyExchange.LabelAccount}</span>
              <span class="value">{account?.label || accountLabel({ accountNum: state.accountNum })}</span>
            </div>
            <div class="confirm-row">
              <span class="label">{i18n.CurrencyExchange.LabelFrom}</span>
              <span class="value">{`${state.fromAmount} ${currencyName(state.fromCurrency)}`}</span>
            </div>
            <div class="confirm-row">
              <span class="label">{i18n.CurrencyExchange.LabelTo}</span>
              <span class="value">{`${state.toAmount} ${currencyName(state.toCurrency)}`}</span>
            </div>
            <div class="confirm-row">
              <span class="label">{i18n.CurrencyExchange.LabelRate}</span>
              <span class="value">{rateLine()}</span>
            </div>
            <div class="confirm-row">
              <span class="label">{i18n.FundH5.Fee}</span>
              <span class="value">0</span>
            </div>
          </div>
          <div class="confirm-disclaimer">{i18n.CurrencyExchange.ConfirmDisclaimer}</div>
          <div class="confirm-actions">
            <button
              type="button"
              class="confirm-cancel"
              disabled={state.submitting}
              onclick={() => {
                if (!state.submitting) sheet.close();
              }}
            >
              {i18n.FundH5.Cancel}
            </button>
            <button
              type="button"
              class="confirm-ok"
              disabled={state.submitting}
              onclick={() => {
                void doSubmit(() => sheet.close());
              }}
            >
              {state.submitting ? <BtnLoading /> : i18n.FundH5.Confirm}
            </button>
          </div>
        </div>
      ),
    });
  };

  const goRecords = () => {
    location.routeTo(exchangeRecordsUrl(state.accountNum));
  };

  const goRecordsFromSuccess = () => {
    const url = exchangeRecordsUrl(state.accountNum, { isBackToApp: true });
    console.log('[currency-exchange] 查看记录 点击', {
      from: 'exchange',
      accountNum: state.accountNum,
      isBackToApp: true,
      targetUrl: url,
      backToAppUrl: getBackToAppUrl(),
      entryQuery: getDepositAppQuery(),
    });
    routeReplace(url);
  };

  const goDeposit = () => {
    location.routeTo(
      withExchangeQuery('/h5/deposits', { accountNum: state.accountNum, currency: state.fromCurrency }),
    );
  };

  const finishSuccess = () => {
    state.mode = 'form';
    state.success = null;
    state.fromAmount = '';
    state.toAmount = '';
    state.fromError = '';
  };

  const hideNav = isFundHideNav();

  return (
    <div class="currency-exchange-h5">
      <If when={!hideNav}>
        <div class="nav">
          <button type="button" class="nav-back" onclick={() => goBackByBrowser()}>
            <IconBack />
          </button>
          <div class="nav-title">{i18n.CurrencyExchange.Title}</div>
          <button type="button" class="nav-right" onclick={goRecords}>
            {i18n.CurrencyExchange.Records}
          </button>
        </div>
      </If>
      <If when={hideNav}>
        <div class="page-record">
          <button type="button" class="nav-right" onclick={goRecords}>
            {i18n.CurrencyExchange.Records}
          </button>
        </div>
      </If>

      <If when={state.mode === 'form'}>
        <div class="exchange-scroll">
          <div class="form">
            <button
              type="button"
              class={`account-trigger${state.accountNum || state.loading ? '' : ' is-empty'}`}
              disabled={state.loading}
              onclick={openAccounts}
            >
              <If when={state.loading}>
                <span class="exchange-loading-bar exchange-loading-account" />
              </If>
              <If when={!state.loading}>
                <span>{accountTriggerText()}</span>
              </If>
              <If when={!state.loading}>
                <img src={downArrow} alt="" />
              </If>
            </button>

            <div class="cards-group">
              <div class={`card${state.fromError ? ' is-error' : ''}`}>
                <div class="card-top">
                  <span class="card-label">{i18n.CurrencyExchange.LabelFrom}</span>
                  <If when={state.loading}>
                    <span class="exchange-loading-bar exchange-loading-bal" />
                  </If>
                  <If when={!state.loading}>
                    <span class="card-hint">
                      {replaceTemplate(i18n.CurrencyExchange.AvailableHint, { bal: availableBal() })}
                    </span>
                  </If>
                </div>
                <div class="card-mid">
                  <button
                    type="button"
                    class="curr-picker"
                    disabled={state.loading}
                    onclick={() => openCurrency('from')}
                  >
                    <If when={state.loading}>
                      <span class="exchange-loading-bar exchange-loading-curr" />
                    </If>
                    <If when={!state.loading}>
                      <>
                        {state.fromCurrency ? <CurrencyFlag code={state.fromCurrency} class="flag" /> : null}
                        <span class={state.fromCurrency ? 'curr-name' : 'curr-ph'}>
                          {state.fromCurrency ? currencyName(state.fromCurrency) : i18n.FundH5.PleaseSelect}
                        </span>
                        <img class="arrow" src={downArrow} alt="" />
                      </>
                    </If>
                  </button>
                  <If when={state.loading}>
                    <span class="exchange-loading-bar exchange-loading-amt" />
                  </If>
                  <If when={!state.loading}>
                    <input
                      class="amt-input"
                      type="text"
                      inputmode="decimal"
                      placeholder="0.00"
                      value={state.fromAmount}
                      oninput={onFromInput}
                    />
                  </If>
                </div>
                <div class="card-bottom">
                  <button type="button" class="top-up" disabled={state.loading} onclick={goDeposit}>
                    {i18n.CurrencyExchange.TopUp}
                  </button>
                </div>
                <If when={!!state.fromError}>
                  <div class="err">{state.fromError}</div>
                </If>
              </div>

              <div class="swap-wrap">
                <button
                  type="button"
                  class="swap-btn"
                  disabled={state.loading}
                  onclick={swapCurrencies}
                >
                  <span
                    class="swap-icon"
                    style={{
                      'mask-image': `url("${exchangeIcon}")`,
                      '-webkit-mask-image': `url("${exchangeIcon}")`,
                    }}
                  />
                </button>
              </div>

              <div class="card card-to">
                <div class="card-top">
                  <span class="card-label">{i18n.CurrencyExchange.ExpectedIn}</span>
                  <span class="card-hint-ph">{i18n.CurrencyExchange.AvailableHintZero}</span>
                </div>
                <div class="card-mid">
                  <button
                    type="button"
                    class="curr-picker"
                    disabled={state.loading}
                    onclick={() => openCurrency('to')}
                  >
                    <If when={state.loading}>
                      <span class="exchange-loading-bar exchange-loading-curr" />
                    </If>
                    <If when={!state.loading}>
                      <>
                        {state.toCurrency ? <CurrencyFlag code={state.toCurrency} class="flag" /> : null}
                        <span class={state.toCurrency ? 'curr-name' : 'curr-ph'}>
                          {state.toCurrency ? currencyName(state.toCurrency) : i18n.FundH5.PleaseSelect}
                        </span>
                        <img class="arrow" src={downArrow} alt="" />
                      </>
                    </If>
                  </button>
                  <If when={state.loading}>
                    <span class="exchange-loading-bar exchange-loading-amt" />
                  </If>
                  <If when={!state.loading}>
                    <input
                      class="amt-input"
                      type="text"
                      inputmode="decimal"
                      placeholder={i18n.CurrencyExchange.PhAutoCalc}
                      value={state.toAmount}
                      oninput={onToInput}
                    />
                  </If>
                </div>
                <div class="card-bottom">
                  <span class="top-up-ph">{i18n.CurrencyExchange.TopUp}</span>
                </div>
              </div>
            </div>

            <If when={!!(state.fromCurrency && state.toCurrency)}>
              <div class="rate-bar">
                <span class={`rate-text${state.rateLoading ? ' is-loading' : ''}`}>{rateLine()}</span>
              </div>
            </If>
          </div>
        </div>

        <div class="footer">
          <button
            type="button"
            class="btn-primary"
            disabled={state.loading || state.submitting || !state.accounts.length}
            onclick={openConfirm}
          >
            {state.submitting ? <BtnLoading /> : i18n.CurrencyExchange.ExchangeBtn}
          </button>
        </div>
      </If>

      <If when={state.mode === 'success' && !!state.success}>
        <div class="exchange-scroll">
          <div class="success">
            <div class="success-hero">
              <Icon name="common-result-success" class="success-result-icon" />
              <div>
                <div class="success-title">{i18n.FundH5.SubmitSuccess}</div>
                <div class="success-desc">{i18n.CurrencyExchange.SubmitSuccessDesc}</div>
              </div>
            </div>
            <div class="success-table">
              <div class="success-labels">
                <span>{i18n.CurrencyExchange.LabelFrom}</span>
                <span>{i18n.CurrencyExchange.LabelTo}</span>
                <span>{i18n.CurrencyExchange.LabelRate}</span>
                <span>{i18n.FundH5.Fee}</span>
              </div>
              <div class="success-values">
                <span>
                  {state.success?.fromAmount} {currencyName(state.success?.fromCurrency || '')}
                </span>
                <span>
                  {state.success?.toAmount} {currencyName(state.success?.toCurrency || '')}
                </span>
                <span>
                  {state.success
                    ? `1 ${currencyName(state.success.fromCurrency)} ≈ ${state.success.rate} ${currencyName(state.success.toCurrency)}`
                    : ''}
                </span>
                <span>0.00</span>
              </div>
            </div>
          </div>
        </div>
        <div class="success-footer">
          <div class="success-footer-actions">
            <button type="button" class="default-btn btn-rounded" onclick={goRecordsFromSuccess}>
              {i18n.FundH5.ViewRecords}
            </button>
            <button type="button" class="primary-btn btn-rounded" onclick={finishSuccess}>
              {i18n.FundH5.Done}
            </button>
          </div>
        </div>
      </If>
    </div>
  );
};

export default CurrencyExchange;
